import React from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupsIcon from '@mui/icons-material/Groups';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import useVisibilityPermissions from '../../hooks/useVisibilityPermissions';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../types/users';

const menuItems = [
  { text: 'Planning', icon: <CalendarMonthIcon />, path: '/calendar', key: 'planning' },
  { text: 'Absences', icon: <EventBusyIcon />, path: '/absences', key: 'absences' },
  { text: 'Techniciens', icon: <PersonIcon />, path: '/technicians', key: 'technicians' },
  { text: 'Compétences', icon: <BuildIcon />, path: '/skills', key: 'skills' },
  { text: 'Statistiques', icon: <BarChartIcon />, path: '/statistics', key: 'statistics' },
  { text: 'Équipes', icon: <GroupsIcon />, path: '/teams', key: 'teams', requiresSuperAdmin: true },
  { text: 'Supervision', icon: <AdminPanelSettingsIcon />, path: '/supervision', key: 'supervision', requiresSuperAdmin: true },
  { text: 'Paramètres', icon: <SettingsIcon />, path: '/settings', key: 'settings' },
];

const Sidebar = ({ open, width }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canViewSidebarItem } = useVisibilityPermissions();
  const { currentUser } = useAuth();

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          top: '64px',
          height: 'calc(100% - 64px)',
          border: 'none',
          backgroundColor: 'background.paper',
          boxShadow: '1px 0 8px rgba(0,0,0,0.05)',
          transition: theme => theme.transitions.create('transform', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          transform: open ? 'none' : `translateX(-${width}px)`,
        },
      }}
    >
      <List disablePadding>
        {menuItems.map((item) => {
          if (item.requiresSuperAdmin && currentUser?.role !== USER_ROLES.SUPER_ADMIN) {
            return null;
          }
          
          if (!canViewSidebarItem(item.key)) {
            return null;
          }

          const isSelected = location.pathname === item.path;
          return (
            <ListItem
              button
              key={item.text}
              onClick={() => navigate(item.path)}
              selected={isSelected}
              sx={{
                py: 1.5,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  bgcolor: 'primary.main',
                  transition: 'transform 0.2s',
                  transform: isSelected ? 'scaleY(1)' : 'scaleY(0)'
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                    transform: 'scale(1.1)',
                  }
                },
                '&.Mui-selected': {
                  backgroundColor: 'primary.lighter',
                  '&:hover': {
                    backgroundColor: 'primary.lighter',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'primary.main',
                    fontWeight: 500
                  }
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  minWidth: 40,
                  transition: 'all 0.2s',
                  color: isSelected ? 'primary.main' : 'text.secondary'
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.95rem',
                    fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary'
                  }
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
